<template>
  <div v-if="metric_clone" :class="[...classes, 'relative']">
    <RecordAutoCompleteInput
      v-if="metric_clone?.reference_name == '_parent' || metric_clone?.reference_name == 'parent__id'"
      :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
      :is-async="true"
      :placeholder="``"
      :label="$t('Parent')"
      :is-box-background="true"
      type="text"
      name="parent"
      label-attr="name"
      classes="w-full block border-none p-0"
      :disabled="record?.type != 'contact'"
      :hyperlink="getRecordDetailsPage"
      :value="metric_clone.value"
      @chosen="(e) => handleSelectParentCompany(e, metric_clone)" />
    <RecordAutoCompleteInput
      v-else-if="metric_clone?.reference_name == 'top_parent'"
      :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
      :is-async="true"
      :hyperlink="getTopRecordDetailsPage"
      :placeholder="``"
      :disabled="true"
      :label="$t('Top Parent')"
      :is-box-background="true"
      type="text"
      label-attr="name"
      name="top_parent"
      classes="w-full block border-none p-0"
      :value="metric_clone.value"
      @chosen="(e) => handleSelectTopParentCompany(e, metric_clone)" />

    <div v-else-if="metric_clone?.input_type.toLowerCase() == 'select'">
      <SelectInput
        :value="metric_clone.value"
        :items="metric_clone?.input_options?.choices"
        :name="metric_clone.reference_name"
        :is-bulk-metric="isBulkMetric"
        :disabled="is_disabled"
        :allow-null="true"
        :label-attr="metric_clone?.input_options?.choices && metric_clone?.input_options?.choices[0]?.label ? 'label' : null"
        :value-attr="metric_clone?.input_options?.choices && metric_clone?.input_options?.choices[0]?.value ? 'value' : null"
        :show-label="showLabel"
        :floating-label="true"
        :is-box-background="true"
        :label="metric_clone.name"
        @update:current-item="handleUpdateValue($event)" />
    </div>

    <!-- Date Input -->
    <div v-else-if="metric_clone.data_type == 'Date' || metric_clone.data_type == 'DateTime'">
      <div
        v-if="showLabel"
        :class="[
          metric_clone?.value ? '-top-3' : 'top-[10px]',
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]"
        class="h-fit px-1 z-[1] font-medium text-sm text-common-grey text-md absolute transform transition-all left-2 ease-in-out duration-side-menu-transition-duration">
        <svg
          v-if="!metric_clone.value"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="absolute w-4 h-full text-gray-500 pointer-events-none">
          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span :class="[metric_clone?.value ? '' : 'ml-6']">{{ metric_clone.name }}</span>
      </div>

      <VueDatePicker
        ref="date_picker"
        v-model="metric_clone.value"
        :hide-input-icon="metric_clone.value"
        :clearable="metric_clone.is_nullable"
        :disabled="!is_editable"
        :enable-time-picker="metric_clone.data_type == 'DateTime'"
        @date-update="moveToSpecificDate"
        @cleared="clearDate" />
    </div>

    <!-- Time input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'time'">
      <input
        :disabled="is_disabled"
        :value="metric_clone.value"
        type="time"
        class="form-input"
        @focusin="input_is_focused = !input_is_focused"
        @focusout="input_is_focused = !input_is_focused"
        @input="
          $utils.debounce(
            () => {
              handleUpdateValue($event.target.value)
              metric_clone.value = $event.target.value
            },
            record ? 300 : 0
          )
        ">
    </div>

    <!-- Email input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'email'">
      <TextInput
        :value="metric_clone.value"
        type="email"
        :name="metric_clone.reference_name"
        :disabled="is_disabled"
        :label="metric_clone.name"
        :show-label="showLabel"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>

    <div v-else-if="metric_clone.input_type.toLowerCase() == 'text'">
      <!-- Text input -->
      <div>
        <TextInput
          :value="metric_clone.value"
          :name="metric_clone.reference_name"
          :show-label="showLabel"
          :disabled="is_disabled"
          :label="metric_clone.name"
          :validate-on-input="record ? false : true"
          :show-success-check-mark="showSuccessCheckMark"
          :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
          :debounce="record ? 300 : 0"
          @update:modelValue="
            ($event) => {
              metric_clone.value = $event
              handleUpdateValue($event)
            }
          " />
      </div>
    </div>

    <!-- TextArea input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'textarea'">
      <TextArea
        :value="metric_clone.value"
        :disabled="is_disabled"
        :label="metric_clone.name"
        :show-label="showLabel"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :name="metric_clone.reference_name"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'richtextarea'">
      <TextEditor
        toolbar="essential"
        :name="metric_clone.reference_name"
        :placeholder="$t('type here')"
        :disabled="is_disabled"
        classes="h-40 w-full"
        content-type="html"
        :label="metric_clone.name"
        :show-label="showLabel"
        :validate-on-input="false"
        input-classes="h-40 bg-white w-full border-2 rounded-md "
        :value="metric_clone.value"
        :theme="metric_clone.input_options?.theme || 'bubble'"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>

    <!-- URL input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'url'">
      <TextInput
        :value="metric_clone.value"
        :name="metric_clone.reference_name"
        :label="metric_clone.name"
        :show-label="showLabel"
        :disabled="is_disabled"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>

    <!-- Multiselect input -->
    <div
      v-else-if="
        metric_clone.input_type.toLowerCase() == 'checklist' ||
          metric_clone.input_type.toLowerCase() == 'bulletpointslist' ||
          metric_clone.input_type.toLowerCase() == 'orderedlist'
      ">
      <div
        v-if="showLabel"
        :class="[
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]"
        class="-top-2 h-fit px-1 z-[1] font-medium text-sm text-common-grey text-md absolute transform transition-all left-2 ease-in-out duration-side-menu-transition-duration">
        {{ metric_clone.name }}
      </div>
      <MetricMultiCheckInput
        :disabled="is_disabled"
        :data="metrics_list"
        :value="selected_options"
        :type="metric_clone.input_type.toLowerCase()"
        @updated="
          ($event) => {
            updateSelectedOptions($event)
          }
        "
        @deleted="
          ($event) => {
            handleListDataTypeDeleteOption($event)
          }
        "
        @submitted="
          ($event) => {
            handleListDataTypeAddOption($event)
          }
        " />
    </div>

    <!-- BarRating input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'barrating'">
      <div
        v-if="showLabel"
        :class="[
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]"
        class="-top-3 h-fit px-1 z-[1] font-medium text-sm text-common-grey text-md absolute transform transition-all left-2 ease-in-out duration-side-menu-transition-duration">
        {{ metric_clone.name }}
      </div>
      <div class="flex w-full bg-white border border-gray-300 rounded-lg dark:border-dark-divider-border dark:bg-dark-box-background">
        <div class="w-full px-2">
          <input
            :disabled="is_disabled"
            :value="metric_clone.value"
            type="range"
            min="0"
            max="10"
            class="w-full h-2 overflow-hidden bg-gray-400 rounded-lg appearance-none"
            @input="
              (e) => {
                handleUpdateValue(e.target.value)
              }
            ">
          <ul class="flex justify-between text-xs text-gray-600 range-labels dark:text-common-grey">
            <li>0</li>
            <li v-for="i in 10" :key="i">
              {{ i }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Star rating input -->
    <div
      v-else-if="metric_clone.input_type.toLowerCase() == 'starrating'"
      class="flex w-full border border-light-divider-border dark:border-dark-divider-border rounded-lg h-full py-2.5">
      <div
        v-if="showLabel"
        :class="[
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]"
        class="-top-3 h-fit px-1 z-[1] font-medium text-sm text-common-grey text-md absolute transform transition-all left-2 ease-in-out duration-side-menu-transition-duration">
        {{ metric_clone.name }}
      </div>
      <div class="flex flex-grow w-full" @click="input_is_focused = true">
        <div class="flex-1" />
        <div class="flex justify-start w-full pl-2">
          <span
            v-for="i in 5"
            :key="i"
            @click="
              () => {
                metric_clone.value = i
                disabled ? null : handleUpdateValue(i)
              }
            ">
            <svg
              class="block w-5 h-5 mr-2"
              :class="[
                metric_clone.value >= i
                  ? metric_clone.value >= 4
                    ? 'text-status-success-green'
                    : metric_clone.value >= 3
                      ? 'text-status-warning-yellow'
                      : 'text-status-error-red'
                  : 'text-gray-300'
              ]"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20">
              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
            </svg>
          </span>
          <div class="h-6 ml-auto mr-2 rounded-full btn cancel-btn" @click="handleUpdateValue(0)">
            {{ $t("Clear") }}
          </div>
        </div>
      </div>
    </div>

    <!-- Checkbox input -->
    <div v-else-if="metric_clone.input_type.toLowerCase() == 'checkbox'" class="py-2">
      <input
        type="checkbox"
        :checked="metric_clone.value"
        :disabled="is_disabled"
        :value="metric_clone.value"
        class="form-checkbox w-6 h-6 rounded-lg text-light-body-text dark:text-dark-body-text outline-none border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue checked:!bg-blue transition ease-in-out duration-side-menu-transition-duration"
        :class="[
          !isBoxBackground
            ? 'bg-light-screen-background dark:bg-dark-screen-background'
            : 'bg-light-box-background dark:bg-dark-box-background'
        ]"
        @click="
          () => {
            metric_clone.value = !metric_clone.value
            handleUpdateValue(metric_clone.value)
          }
        ">
      <label class="ml-2 text-light-body-text dark:text-dark-body-text">{{ metric_clone.name }}</label>
    </div>

    <div
      v-else-if="
        metric_clone.input_type.toLowerCase() == 'decimal' ||
          metric_clone.input_type.toLowerCase() == 'currency' ||
          metric_clone.input_type.toLowerCase() == 'percent'
      ">
      <TextInput
        :value="metric_clone.value"
        :type="metric_clone.input_type.toLowerCase()"
        :input-options="metric_clone.input_options"
        :name="metric_clone.reference_name"
        :disabled="is_disabled"
        :label="metric_clone.name"
        :show-label="showLabel"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>

    <div v-else-if="metric_clone.input_type.toLowerCase() == 'number'">
      <TextInput
        :value="metric_clone.value"
        :name="metric_clone.reference_name"
        :disabled="is_disabled"
        type="number"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :label="metric_clone.name"
        :show-label="showLabel"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>

    <!-- String input -->
    <div v-else-if="metric_clone.data_type == 'Json'">
      <TextInput
        :name="metric_clone.reference_name"
        :value="metric_clone.value?.['name']"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :label="metric_clone.name"
        :disabled="true"
        :show-label="showLabel"
        :validate-on-input="false" />
    </div>
    <div v-else>
      <TextInput
        :value="metric_clone.value"
        :name="metric_clone.reference_name"
        :show-success-check-mark="showSuccessCheckMark"
        :hide-success-check-mark-after="hideSuccessCheckMarkAfter"
        :label="metric_clone.name"
        :disabled="is_disabled"
        :show-label="showLabel"
        :validate-on-input="record ? false : true"
        :debounce="record ? 300 : 0"
        @update:modelValue="
          ($event) => {
            metric_clone.value = $event
            handleUpdateValue($event)
          }
        " />
    </div>
    <div v-if="is_disabled" class="absolute bg-gray-100 cursor-pointer right-2 top-2 has-tooltip">
      <Info class="w-6 h-6 text-common-grey" aria-hidden="true" />
      <div
        class="flex gap-2 px-2 py-2 text-sm font-medium border rounded-md tooltip -ml-36 border-dark-brand-blue dark:border-dark-grey-text bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
        <span v-if="metric_clone.aggregation_method">{{
          $t("This field is calculated automatically and is read-only and can not be edited")
        }}</span>
        <span v-else>{{ $t("This field is read-only and can not be edited") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker"
import SelectInput from "@/components/shared/SelectInput.vue"
import dataService from "@/services/data.service"
import MetricMultiCheckInput from "@/components/shared/MetricMultiCheckInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import * as Yup from "yup"
import TextInput from "./TextInput.vue"
import { utils } from "@/store/helpers"
import { Info } from "lucide-vue-next"
import TextEditor from "@/components/shared/TextEditor.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import "@vuepic/vue-datepicker/dist/main.css"
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"

export default {
  components: {
    RecordAutoCompleteInput,
    TextEditor,
    Info,
    SelectInput,
    TextInput,
    VueDatePicker,
    MetricMultiCheckInput,
    TextArea
  },

  props: {
    isBulkMetric: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    updateValue: {
      type: Boolean,
      default: true
    },
    showSuccessCheckMark: {
      type: Boolean,
      default: false
    },
    hideSuccessCheckMarkAfter: {
      type: Number,
      default: null
    },
    classes: {
      type: String,
      default: "text"
    },
    metric: {
      type: Object,
      required: true
    },
    metricValue: {
      type: [String, Number, Boolean, Array, Object, Date, null],
      required: false,
      default: null
    },
    record: {
      type: Object,
      required: false,
      default: null
    },
    isBoxBackground: {
      type: Boolean,
      default: true
    },
    componentReference: {
      type: String,
      default: null,
      required: false
    }
  },
  emits: ["update:metricValue", "updated", "validation"],
  data() {
    return {
      metric_clone: null,
      metric_backup_value: null,
      metric_new_value: null,
      input_is_focused: false,
      selected_options: [],
      metrics_list: []
    }
  },
  computed: {
    is_editable() {
      return this.metric_clone.is_nullable && !this.metric_clone.is_read_only && !this.metric_clone.is_rs_readonly && !this.disabled
    },
    is_disabled() {
      return ((this.metric_clone.is_read_only || this.metric_clone.is_rs_readonly) && this.record) || this.disabled
    }
  },
  watch: {
    metric: {
      deep: true,
      handler() {
        if (this.metric) {
          this.metric_clone = this.$utils.clone(this.metric)
        }
      }
    }
  },
  created() {
    this.register_listeners()

    this.metric_clone = this.$utils.clone(this.metric) || {
      name: "",
      value: "",
      input_type: "",
      data_type: ""
    }

    this.metric_clone.value = this.metric?.value || this.metricValue

    if (
      this.metric_clone?.input_type.toLowerCase() == "checklist" ||
      this.metric_clone?.input_type.toLowerCase() == "bulletpointslist" ||
      this.metric_clone?.input_type.toLowerCase() == "orderedlist"
    ) {
      this.metric_clone.value
        ? Array.isArray(this.metric_clone.value)
          ? (this.selected_options = this.metric_clone.value)
          : (this.selected_options = this.metric_clone.value.split(","))
        : []
    }

    if (Array.isArray(this.metric_clone?.input_options?.choices)) {
      this.metrics_list = this.metric_clone?.input_options?.choices
    } else {
      this.metrics_list = []
    }
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    getRecordDetailsPage() {
      if (this.record.parent) {
        //Last visited records
        this.$store.dispatch("data/getRecord", this.record.parent).then((res) => {
          const parent_record = res.data

          if (parent_record.type == "company") {
            this.$store.dispatch("ui/setCurrentCompany", parent_record)
            this.$store.dispatch("ui/setCurrentCompanyBreadcrumb", this.$utils.getRecordName(parent_record))
            this.$router.push(`/portfolio/company/${parent_record.id}`)
          } else {
            if (this.$store.state.data.records_fetched[parent_record.id]) {
              this.$router.push(
                `/portfolio/company/${this.$store.state.data.records_fetched[parent_record.id].parent}/${parent_record.type}/${parent_record.id}`
              )
            } else {
              this.$store.dispatch("data/getRecord", parent_record.id).then((res) => {
                this.$router.push(`/portfolio/company/${res.data.parent}/${parent_record.type}/${parent_record.id}`)
              })
            }
          }
        })
      }
    },

    getTopRecordDetailsPage() {
      if (this.record.top_parent) {
        //Last visited records
        this.$store.dispatch("data/getRecord", this.record.top_parent).then((res) => {
          const top_parent_record = res.data

          if (top_parent_record.type == "company") {
            this.$store.dispatch("ui/setCurrentCompany", top_parent_record)
            this.$store.dispatch("ui/setCurrentCompanyBreadcrumb", this.$utils.getRecordName(top_parent_record))
            this.$router.push(`/company/${top_parent_record.id}`)
          } else {
            if (this.$store.state.data.records_fetched[top_parent_record.id]) {
              this.$router.push(
                `/company/${
                  this.$store.state.data.records_fetched[top_parent_record.id].parent
                }/${top_parent_record.type}/${top_parent_record.id}`
              )
            } else {
              this.$store.dispatch("data/getRecord", top_parent_record.id).then((res) => {
                this.$router.push(`/company/${res.data.parent}/${top_parent_record.type}/${top_parent_record.id}`)
              })
            }
          }
        })
      }
    },

    handleSelectParentCompany(e, metric) {
      metric.value = e.id
      this.$store
        .dispatch("data/updateRecord", {
          record_id: this.record.id,
          data: {
            parent: e.id
          }
        })
        .then(() => {
          // this.$emitter.emit(ALL_EMITTERS.UPDATE_KEY_RECORDS_TIED_RECORD)
        })
    },

    handleSelectTopParentCompany(e, metric) {
      metric.value = e.id
      this.$store
        .dispatch("data/updateRecord", {
          record_id: this.record.id,
          data: {
            parent: e.id
          }
        })
        .then(() => {
          // this.$emitter.emit(ALL_EMITTERS.UPDATE_KEY_RECORDS_TIED_RECORD)
        })
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.LIST_METRICS_CHANGED, ({ metric_obj }) => {
        this.metrics_list = metric_obj?.input_options?.choices || []
        if (metric_obj.value && typeof metric_obj.value == "string") {
          this.selected_options = metric_obj.value.split(",")
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.LIST_METRICS_CHANGED)
    },
    async isMetricValueValid(v) {
      if (["boolean", "starrating", "checkbox"].includes(this.metric_clone.input_type.toLowerCase())) {
        return true
      } else if (
        this.metric_clone.id == this.$store.state.integration.metrics_mapping["company_name_metric"] &&
        this.$utils.isEmpty(this.metric_clone.value)
      ) {
        //Prevent user from deleting company name
        return false
      } else if (this.metric_clone.data_type == "List") {
        if (!Array.isArray(v)) {
          return false
        }
        if (this.metric_clone.input_options?.min_items && this.metric_clone.input_options?.min_items > v.length) {
          return false
        }
        if (this.metric_clone.input_options?.max_items && this.metric_clone.input_options?.max_items < v.length) {
          return false
        }
        return true
      } else if (
        this.metric_clone.input_type.toLowerCase() == "date" ||
        this.metric_clone.input_type.toLowerCase() == "datetime" ||
        this.metric_clone.input_type.toLowerCase() == "time"
      ) {
        if (this.$utils.isEmpty(v) && this.metric_clone.is_nullable) {
          return true
        }
        return await this.$moment(v, true).isValid()
      } else if (this.$utils.isEmpty(this.metric_clone.value)) {
        return true
      } else if (this.metric_clone.input_type == "Select") {
        return true
      } else if (this.metric_clone.input_type.toLowerCase() == "url") {
        return await Yup.string()
          .matches(/((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/)
          .nullable()
          .isValid(v)
      } else if (this.metric_clone.input_type.toLowerCase() == "number") {
        return await Yup.number(this.$t("Please enter a valid number")).nullable().isValid(v)
      } else if (this.metric_clone.input_type.toLowerCase() == "email") {
        return await Yup.string().email().nullable().isValid(v)
      }

      return await Yup.string().nullable().isValid(v)
    },

    handleListDataTypeAddOption(value) {
      /* New item added to the list of options */
      this.selected_options.push(value)
      this.metrics_list.push(value)
      const metric_obj = {
        ...this.metric,
        input_options: { choices: this.metrics_list }
      }

      dataService.updateOrganizationMetric(metric_obj).then(() => {
        this.$store.dispatch("data/getMetrics")
        this.$emitter.emit(ALL_EMITTERS.LIST_METRICS_CHANGED, { metric_obj })
        this.metric_new_value = this.selected_options
        this.$emit("update:metricValue", this.metric_new_value)
        this.handleUpdateValue(this.metric_new_value)
      })
    },

    handleListDataTypeDeleteOption(value) {
      /* New item deleted from the list of options */
      this.selected_options = this.selected_options.filter((v) => v !== value)
      this.metrics_list = this.metrics_list.filter((v) => v !== value)
      const metric_obj = {
        ...this.metric,
        input_options: { choices: this.metrics_list }
      }
      dataService.updateOrganizationMetric(metric_obj).then(() => {
        this.$store.dispatch("data/getMetrics")
        this.$emitter.emit(ALL_EMITTERS.LIST_METRICS_CHANGED, { metric_obj })
        this.$emit("updated")
      })
    },
    updateSelectedOptions(list) {
      this.selected_options = list
      this.metric_new_value = this.selected_options
      this.$emit("update:metricValue", this.metric_new_value)
      this.handleUpdateValue(this.metric_new_value)
    },
    moveToSpecificDate(date) {
      this.metric_new_value = date
      this.$refs.date_picker.closeMenu()
      // Update the parent component with the new value
      this.$emit("update:metricValue", this.metric_new_value)

      this.handleUpdateValue(this.metric_new_value)
    },

    clearDate() {
      this.$refs.date_picker.closeMenu()
      this.metric_new_value = null
      this.$emit("update:metricValue", this.metric_new_value)
      // this will trigger the change in the metrics overview page (ie no submit button, auto save on change)
      this.handleUpdateValue(this.metric_new_value)
    },

    handleUpdateValue(e) {
      if (!this.updateValue) {
        return
      }
      let rawValue
      if (
        this.metric_clone.input_type.toLowerCase() == "date" ||
        this.metric_clone.input_type.toLowerCase() == "datetime" ||
        this.metric_clone.input_type.toLowerCase() == "time"
      ) {
        rawValue = this.metric_new_value
      } else if (!e && this.metric_clone.input_type.toLowerCase() == "select") {
        rawValue = null
      } else if (!e && this.metric_clone.input_type.toLowerCase() == "starrating") {
        rawValue = 0
      } else if (this.metric_clone?.data_type?.toLowerCase() == "list") {
        rawValue = this.metric_new_value
      } else {
        rawValue = e?.target?.value ? e.target.value : e ? e : this.metric_clone?.value
      }

      this.isMetricValueValid(rawValue).then((is_valid) => {
        this.$emit("validation", is_valid)

        if (is_valid) {
          this.metric_new_value = rawValue
          if (utils.isNumber(this.metric_new_value) && this.metric_clone.input_type.toLowerCase() != "textarea") {
            // convert to number if the metric is a number or decimal
            if (this.metric_clone.input_type == "Decimal") {
              this.metric_new_value = parseFloat(this.metric_new_value)
            } else if (this.metric_clone?.data_type == "Number") {
              this.metric_new_value = parseInt(this.metric_new_value)
            }
          }
          this.$emit("update:metricValue", this.metric_new_value)
          this.updateMetricValue()
        }
      })
    },
    updateMetricValue() {
      if (this.record) {
        let value = this.metric_new_value
        // if date before send to server we convert to utc always
        if (this.metric_clone.data_type == "DateTime" || this.metric_clone.data_type == "Date") {
          value = utils.convertDateStringFromLocalDateToUtc(value, this.metric_clone.data_type)
        }
        this.$store
          .dispatch("data/updateRecordMetric", {
            record_id: this.record.id,
            data: {
              prev_value: this.metric_backup_value,
              new_value: value,
              metric: this.metric_clone.id
            }
          })
          .then((res) => {
            this.metric_clone.value = this.metric_new_value

            if (this.$store.state.ui.currentCompany.id == this.record.id) {
              this.$store.dispatch("ui/setCurrentCompany", res.data)
            }

            // update record in the store
            this.$store.commit("data/setRecord", res.data)
            this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_CUSTOMER_JOURNEYS_VIEW)
            this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS)
            this.$emitter.emit(ALL_EMITTERS.METRICS_CHANGED)
            this.$emitter.emit(ALL_EMITTERS.METRIC_UPDATED_IN_GRAPHS_GRID, this.metric_clone)
            this.$emitter.emit(ALL_EMITTERS.METRICS_UPDATED_IN_OBJECT_VIEW, this.metric_clone)

            if (this.$route.name !== "WorkspaceItem") {
              this.$store.dispatch("data/emitRecordUpdatedEmitters", res.data)
            }
            this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, this.componentReference)
            this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, this.record.id)
            this.$emit("updated")

            this.$emit("update:metricValue", this.metric_new_value)
          })
          .catch((error) => {
            let error_message = this.$t("Failed to update") + ` ${this.metric_clone.name} ` + this.$t("metric value. Please try again.")
            if (error?.code == "BAD_REQUEST") {
              error_message = this.$t("Failed to update") + ` ${this.metric_clone.name} ` + this.$t("metric value") + " .<br>"
              Object.keys(error.errors).forEach((key) => {
                error_message = error_message + " " + error.errors[key] + "<br>"
              })
            }
          })
      } else {
        this.$emit("update:metricValue", this.metric_new_value)
      }
    }
  }
}
</script>

<style scoped>
input:invalid {
  border: 2px solid red;
}

input[type="range"]::-webkit-slider-thumb {
  width: 10px;
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  cursor: ew-resize;
  background: #ffffff;
  box-shadow: -405px 0 0 400px #605e5c;
  border-radius: 50%;
}
</style>
